import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminApi } from "@/lib/api/admin";
import { useAuthStore } from "@/stores/auth-store";
import { Button } from "@/components/ui/button";
import { LogoMark, Wordmark } from "@/components/brand";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});
type FormValues = z.infer<typeof schema>;

/** BitPays admin login — react-hook-form + zod → NestJS /v1/auth/admin/login. */
export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const res = await adminApi.login(values.email, values.password);
      // Clear any cached data from a previous session in this browser so a new
      // user never sees the prior user's data (e.g. a subscriber must never see
      // the platform admin's dashboard, even for a flash).
      queryClient.clear();
      setAuth(res.access_token, res.admin);
      // Land each role on a page it can actually access.
      const role = res.admin?.role ?? "";
      const home = role === "PROVIDER_ADMIN" ? "/disputes" : "/dashboard";
      navigate(home);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden overflow-hidden bg-sidebar p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bp-gradient opacity-30 blur-3xl" />
        <div className="flex items-center gap-3">
          <LogoMark className="h-10 w-10" />
          <Wordmark on="dark" className="text-2xl" />
        </div>
        <div className="relative">
          <h2 className="max-w-md text-2xl font-bold leading-tight">
            Payments infrastructure for the modern web.
          </h2>
          <p className="mt-3 max-w-md text-sm text-white/60">
            Route, settle and reconcile — all from one console. bitpays.live
          </p>
        </div>
        <span className="text-xs text-white/40">© BitPays</span>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <LogoMark className="h-9 w-9" />
            <Wordmark />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">Sign in</h1>
          <p className="mb-6 mt-1 text-sm text-muted-foreground">Access your BitPays admin console</p>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Email</label>
              <input
                className="w-full rounded-xl border bg-transparent px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40"
                type="email"
                {...register("email")}
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Password</label>
              <input
                className="w-full rounded-xl border bg-transparent px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40"
                type="password"
                {...register("password")}
              />
              {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
            </div>
            <Button type="submit" disabled={submitting} className="mt-2">
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
